import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import usersAPI from '../api/users';
import threadsAPI from '../api/threads';
import './UserProfile.css';

const UserProfile = () => {
  const { user_id, username } = useParams();
  const [user, setUser] = useState(null);
  const [threads, setThreads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingThreads, setIsLoadingThreads] = useState(false);
  const [error, setError] = useState('');
  const [threadsError, setThreadsError] = useState('');

  const formatTimeAgo = (isoString) => {
    if (!isoString) return '';
    const now = new Date();
    const then = new Date(isoString);
    const seconds = Math.max(1, Math.floor((now.getTime() - then.getTime()) / 1000));

    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (seconds < 60) return `${seconds}s ago`;
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    if (weeks < 5) return `${weeks}w ago`;
    if (months < 12) return `${months}mo ago`;
    return `${years}y ago`;
  };

  const getUserInitials = (user) => {
    if (!user) return 'U';
    const source = (user.username || user.email || 'U').trim();
    const parts = source.replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
    const letters = parts.length >= 2 ? parts[0][0] + parts[1][0] : source[0];
    return letters.toUpperCase();
  };

  const getDisplayUsername = (user) => {
    if (!user) return 'User';
    if (user.username) return user.username;
    if (user.email) return user.email;
    return 'User';
  };

  const handleThreadClick = (threadId) => {
    const baseUrl = window.location.origin;
    const shareLink = `${baseUrl}/share/chat/thread/${threadId}`;
    window.open(shareLink, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    const loadUserProfile = async () => {
      setIsLoading(true);
      setError('');
      try {
        const userData = await usersAPI.getUserProfilePublic(user_id);
        setUser(userData);
      } catch (err) {
        console.error('Failed to fetch user profile:', err);
        setError('Failed to load user profile');
      } finally {
        setIsLoading(false);
      }
    };

    if (user_id) {
      loadUserProfile();
    }
  }, [user_id]);

  useEffect(() => {
    const loadUserThreads = async () => {
      if (!user_id) return;
      setIsLoadingThreads(true);
      setThreadsError('');
      try {
        const threadsData = await threadsAPI.getPublicThreadsByOwner(user_id);
        const allThreads = Array.isArray(threadsData) ? threadsData : [];
        // Filter to only show active threads
        const activeThreads = allThreads.filter(thread => thread.is_active === true);
        setThreads(activeThreads);
      } catch (err) {
        console.error('Failed to fetch user threads:', err);
        setThreadsError('Failed to load threads');
      } finally {
        setIsLoadingThreads(false);
      }
    };

    if (user_id) {
      loadUserThreads();
    }
  }, [user_id]);

  if (isLoading) {
    return (
      <div className="user-profile-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-profile-container">
        <div className="error-container">
          <div className="error-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <h2>Profile Not Found</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-profile-container">
        <div className="error-container">
          <div className="error-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <h2>User Not Found</h2>
          <p>The requested user profile could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile-container">
      <div className="user-profile-content">
        {/* Profile Header */}
        <div className="profile-header">
          <div className="profile-picture-container">
            {user.profile_picture_url ? (
              <img 
                src={user.profile_picture_url} 
                alt="Profile" 
                className="profile-picture-image"
              />
            ) : (
              <div className="profile-picture-placeholder">
                {getUserInitials(user)}
              </div>
            )}
          </div>
          <div className="profile-info">
            <h1 className="profile-username">{getDisplayUsername(user)}</h1>
            <div className="profile-meta">
              <span className="profile-thread-count">
                {threads.length} active thread{threads.length === 1 ? '' : 's'}
              </span>
            </div>
          </div>
        </div>

        {/* Threads Section */}
        <div className="threads-section">
          <div className="threads-header">
            <h2 className="threads-title">Active Threads</h2>
            <div className="threads-count">{threads.length} thread{threads.length === 1 ? '' : 's'}</div>
          </div>
          
          {threadsError && <div className="error-message">{threadsError}</div>}
          
          {isLoadingThreads ? (
            <div className="loading-inline">Loading threads...</div>
          ) : threads.length === 0 ? (
            <div className="no-threads">
              <div className="no-threads-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
              </div>
              <p>No active threads available</p>
            </div>
          ) : (
            <div className="threads-grid">
              {threads.map((thread) => (
                <div
                  key={thread.id}
                  className="thread-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleThreadClick(thread.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleThreadClick(thread.id);
                    }
                  }}
                >
                  <div className="thread-card-body">
                    <div className="thread-title">{thread.label || 'Untitled thread'}</div>
                    <div className="thread-meta">
                      <span className="thread-meta-item">
                        {(Array.isArray(thread.conversation_sessions) ? thread.conversation_sessions.length : 0)} session{(Array.isArray(thread.conversation_sessions) ? thread.conversation_sessions.length : 0) === 1 ? '' : 's'}
                      </span>
                      {thread.created_at && (
                        <>
                          <span className="thread-meta-sep">•</span>
                          <span className="thread-meta-item" title={new Date(thread.created_at).toLocaleString()}>
                            {formatTimeAgo(thread.created_at)}
                          </span>
                        </>
                      )}
                      <span className="thread-meta-sep">•</span>
                      <span className="thread-status active">
                        Active
                      </span>
                    </div>
                    {thread.system_prompt && (
                      <div className="thread-description">
                        {thread.system_prompt.length > 100 
                          ? `${thread.system_prompt.substring(0, 100)}...` 
                          : thread.system_prompt
                        }
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
